import { Box, Typography } from '@material-ui/core';
import {
    Cartesian2,
    Cartesian3,
    Cartographic,
    ClockRange,
    ClockStep,
    Color,
    defined,
    DistanceDisplayCondition,
    HorizontalOrigin,
    JulianDate,
    Material,
    NearFarScalar,
    SampledPositionProperty,
    TimeInterval,
    TimeIntervalCollection,
    VelocityOrientationProperty,
    VerticalOrigin,
    Viewer as CesiumViewer,
    Math as CesiumMath,
    HeightReference,
    SampledProperty,
    Ion,
    GeometryInstance,
    GroundPolylineGeometry,
    ColorGeometryInstanceAttribute,
    PolylineColorAppearance,
} from 'cesium';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    CesiumComponentRef,
    Clock,
    Entity,
    useCesium,
    Viewer,
    EntityDescription,
    CameraFlyTo,
    GroundPolylinePrimitive,
} from 'resium';
import buildPositionProperty, {
    CustomGeopoint,
    terrainProvider,
} from '../../util/buildPositionProperty';
import sanityClient from '../../util/sanityClient';
import PointEntities, {
    Hike,
} from '../PointEntities/PointEntities';


Ion.defaultAccessToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI4MGE1MzVlYy1kZjMxLTQxMWUtODM4MS03OGE1NjRlOWRlNTUiLCJpZCI6NzIzNTEsImlhdCI6MTYzNTk2MzQ3MH0.Opk0SYvfjQB8kYJl6eauLtXev4dvwf1sgnT0HrqATSA';

interface MapDataResponse {
  points: Array<CustomGeopoint>;
  hike: Hike;
}

const HikerDescription: React.FC<{ startTime: JulianDate }> = ({
    startTime,
}) => {
    const [alt, setAlt] = React.useState<string | undefined>('');
    const [lat, setLat] = React.useState<string | undefined>('');
    const [lon, setLon] = React.useState<string | undefined>('');
    const [cumul, setCumul] = React.useState<string | undefined>('');

    const [hikeTime, setHikeTime] = React.useState<string | undefined>('00:00:00');

    const { viewer } = useCesium();

    useEffect(() => {
        if (viewer) {
            const hikerEntity = viewer.entities.values.find((ent) => ent.name === 'Hiker');
            
            viewer.clock.onTick.addEventListener((clock) => {
                if (startTime) {
                    const seconds = JulianDate.secondsDifference(clock.currentTime, startTime);
                    const time = new Date(seconds * 1000).toISOString().substr(11, 8);
                    setHikeTime(time);
                }


                if (hikerEntity) {
                    const cartesian = hikerEntity.position?.getValue(clock.currentTime);
                    const properties = hikerEntity.properties?.getValue(clock.currentTime);
    
                    if (defined(cartesian) && cartesian) {
                        const cartographic = Cartographic.fromCartesian(cartesian);
                        const longitudeString = CesiumMath.toDegrees(cartographic.longitude).toFixed(3);
    
                        const latitudeString = CesiumMath.toDegrees(
                            cartographic.latitude
                        ).toFixed(3);
    
                        const heightString = (cartographic.height * 3.28084).toFixed(0);
    
                        if (hikerEntity && hikerEntity.label) {
                            setAlt(heightString);
                            setLat(latitudeString);
                            setLon(longitudeString);
                        }
                    }

                    if (properties) {
                        const convertToMiles = (0.000621371 * properties.cumul).toFixed(2);
                        setCumul(convertToMiles);
                    }
                }
            });
            viewer.selectedEntity = hikerEntity;
        }
    }, []);
    return (
        <EntityDescription>
            <Box height="auto" minHeight="200px">
                <Typography>{lat}&deg;, {lon}&deg;</Typography>
                <Typography>Altitude: {alt} ft.</Typography>
                <Typography>Time Elapsed: {hikeTime}</Typography>
                <Typography>Cumulative Distance: {cumul} mi.</Typography>
            </Box>
        </EntityDescription>
    );
};

const CesiumMap = () => {
    const [startTime, setStartTime] = useState(
        JulianDate.fromDate(new Date('2021-08-14T17:31:55.000Z'))
    );
    const [stopTime, setStopTime] = useState(
        JulianDate.fromDate(new Date('2021-08-14T21:01:59.000Z'))
    );
    const [positionProperty, setPositionProperty] =
    useState<SampledPositionProperty | null>(null);
    const [hikePoints, setHikePoints] = useState<Array<Cartesian3> | null>(null);
    const [sampledCumulProperty, setSampledCumulProperty] = useState<SampledProperty | null>(null);
    const [hike, setHike] = useState<Hike | null>(null);
    const [mapData, setMapData] = React.useState<MapDataResponse | undefined>();
    const { id } = useParams<{ id: string }>();

    const query =
    '*[_id == $id][0]{ points,   "hike": *[ _type == "hike" &&  references(^._id)][0]{destination-> {height,lat,link,lon,name,image {asset->} }, startingPoint-> { height, lat, link, lon, name, image { asset-> } }, places[]-> { height,lat, link, lon,name,image {asset->}},...}}';
    const params = React.useMemo(() => ({ id }), [id]);

    const fetchMapData = React.useCallback(() => {
        sanityClient
            .fetch(query, params)
            .then((result: MapDataResponse) => {
                if (result) {
                    setMapData(result);
                } else {
                    throw new Error(`Map data not found for ${id}`);
                }
            })
            .catch(() => {
                throw new Error('Something went wrong calling Sanity.io.');
            });
    }, [query, params, id]);

    React.useEffect(() => {
        fetchMapData();
    }, [fetchMapData]);

    useEffect(() => {
        if (mapData) {
            buildPositionProperty(mapData.points).then((returnedPositionProperty) => {
                setStartTime(JulianDate.fromDate(new Date(mapData.points[0].time)));
                setStopTime(
                    JulianDate.fromDate(
                        new Date(mapData.points[mapData.points.length - 1].time)
                    )
                );
                setPositionProperty(returnedPositionProperty[1]);
                setHikePoints(returnedPositionProperty[0]);
                setSampledCumulProperty(returnedPositionProperty[2]);
            });

            if (mapData.hike) {
                setHike(mapData.hike);
            }
        }
    }, [mapData]);

    const ref = useRef<CesiumComponentRef<CesiumViewer>>(null);

    useEffect(() => {
        if (hikePoints && positionProperty && ref.current?.cesiumElement) {
            /**
       * Update the timeline
       */
            const { cesiumElement } = ref.current;

            const { timeline } = cesiumElement;
 
            timeline.zoomTo(startTime, stopTime);
        }
    }, [hikePoints, positionProperty, ref.current?.cesiumElement]);

    useEffect(() => {
        if (ref.current?.cesiumElement) {
            /**
       * Set the clock to Pacific time
       */
            ref.current.cesiumElement.animation.viewModel.timeFormatter = function (
                date,
                viewModel
            ) {
                let newDate = JulianDate.toDate(date);
                let toPacific = new Intl.DateTimeFormat('en-US', {
                    timeZone: 'America/Los_Angeles',
                    hour: 'numeric',
                    minute: 'numeric',
                    second: 'numeric',
                }).format(newDate);

                return toPacific;
            };

        }
    }, []);

    const getFlyToDestinationHeightAdjustment = (starting: CustomGeopoint) => {
        return Cartesian3.fromDegrees(starting.lng, starting.lat, 10000);
    };

    return (
        <Viewer ref={ref} requestRenderMode full terrainProvider={terrainProvider}>
            <Clock
                startTime={startTime.clone()}
                currentTime={startTime.clone()}
                stopTime={stopTime.clone()}
                canAnimate
                clockStep={ClockStep.SYSTEM_CLOCK_MULTIPLIER}
                clockRange={ClockRange.CLAMPED}
                multiplier={50}
            />
            {positionProperty && sampledCumulProperty && (
                <Entity
                    name="Hiker"
                    tracked={true}
                    orientation={new VelocityOrientationProperty(positionProperty)}
                    position={positionProperty}
                    label={{
                        distanceDisplayCondition: new DistanceDisplayCondition(0, 25000),
                        scaleByDistance: new NearFarScalar(0, 1.3, 25000, 0),
                        pixelOffsetScaleByDistance: undefined,
                        show: true,
                        showBackground: true,
                        font: '14px monospace',
                        horizontalOrigin: HorizontalOrigin.CENTER,
                        verticalOrigin: VerticalOrigin.BOTTOM,
                        pixelOffset: new Cartesian2(0, -45),
                        heightReference: HeightReference.CLAMP_TO_GROUND,
                    }}
                    point={{
                        pixelSize: 20,
                        color: Color.RED,
                        scaleByDistance: new NearFarScalar(0, 1, 100000, 0),
                    }}
                    availability={
                        new TimeIntervalCollection([
                            new TimeInterval({
                                start: startTime,
                                stop: stopTime,
                            }),
                        ])
                    }
                    properties={{
                        cumul: sampledCumulProperty
                    }}
                >
                    <HikerDescription startTime={startTime} />
                </Entity>
            )}
            {hikePoints && (
                <GroundPolylinePrimitive 
                    geometryInstances={new GeometryInstance({
                        geometry: new GroundPolylineGeometry({
                            positions: hikePoints,
                            width: 2.0
                        }),
                        attributes: {
                            color: ColorGeometryInstanceAttribute.fromColor(Color.RED.withAlpha(1.0))
                        }
                    })}
                    show={true}
                    appearance={new PolylineColorAppearance()}
                />
            )}
            <PointEntities hike={hike} />
            {mapData && (
                <CameraFlyTo 
                    once
                    destination={getFlyToDestinationHeightAdjustment(mapData.points[0])}   
                    orientation={{
                        heading: CesiumMath.toRadians(20.0),
                        pitch: CesiumMath.toRadians(-90),
                        roll: 0.0,
                    }}
                />
            )}
        </Viewer>
    );
};

export default CesiumMap;
